/**
 * Contratos ganhos: consultas e mutações em /app/contratos, mais os rótulos e cálculos de vigência
 * que a lista e o formulário usam. Dinheiro em centavos (como no contrato).
 */
import type { Contrato, ContratoInput, ContratosResponse, CONTRATO_STATUSES } from '@licita/contracts';
import { CONTRATO_STATUS_NOMES } from '@licita/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api } from './api';
import { queryClient } from './queries';
import type { Tone } from './app';

export type ContratoStatus = (typeof CONTRATO_STATUSES)[number];
type ContratosResponse = ReturnType<typeof ContratosResponse.parse>;

export const contratosQuery = queryOptions({
  queryKey: ['contratos'],
  queryFn: () => api<ContratosResponse>('/app/contratos'),
});

/** POST (novo) ou PUT (edição); devolve o contrato salvo e invalida a lista. */
export async function salvarContrato(id: string | null, body: ContratoInput): Promise<Contrato> {
  const c = id
    ? await api<Contrato>(`/app/contratos/${id}`, { method: 'PUT', json: body })
    : await api<Contrato>('/app/contratos', { method: 'POST', json: body });
  await queryClient.invalidateQueries({ queryKey: ['contratos'] });
  return c;
}

export async function removerContrato(id: string): Promise<void> {
  await api<{ ok: true }>(`/app/contratos/${id}`, { method: 'DELETE' });
  await queryClient.invalidateQueries({ queryKey: ['contratos'] });
}

// ---------- rótulos e cálculos ----------

export const STATUS_NOME = CONTRATO_STATUS_NOMES;
export const STATUS_TONE: Record<ContratoStatus, Tone> = { vigente: 'ok', encerrado: 'muted', rescindido: 'bad', suspenso: 'warn' };

/** Centavos → "R$ 1.234,56". */
export const money = (minor: number | null | undefined) =>
  minor == null ? '—' : (minor / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/\u00a0/g, ' ');

/** "2026-10-20" → "20/10/2026" (sem passar por Date, para não mudar de dia com o fuso). */
export const dataISO = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

/** Dias de hoje até a data (negativo = já passou); a data é lida como dia local. */
export function diasAte(iso: string, agora = new Date()): number {
  const [a, m, d] = iso.split('-').map(Number);
  const alvo = new Date(a!, m! - 1, d!);
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  return Math.round((alvo.getTime() - hoje.getTime()) / 86_400_000);
}

/** Aviso de vigência: fim nos próximos 60 dias ou já passado. Não vale para contratos encerrados ou rescindidos. */
export function alertaVigencia(c: Pick<Contrato, 'vigenciaFim' | 'status'>, agora = new Date()): { texto: string; tone: Tone } | null {
  if (!c.vigenciaFim || c.status === 'encerrado' || c.status === 'rescindido') return null;
  const dias = diasAte(c.vigenciaFim, agora);
  if (dias < 0) return { texto: dias === -1 ? 'venceu ontem' : `venceu há ${-dias} dias`, tone: 'bad' };
  if (dias === 0) return { texto: 'vence hoje', tone: 'bad' };
  if (dias === 1) return { texto: 'vence amanhã', tone: 'bad' };
  if (dias <= 60) return { texto: `vence em ${dias} dias`, tone: 'warn' };
  return null;
}
